import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * A custom element that displays a variant-reactive Size & Fit measurements
 * table. Reads an embedded JSON blob keyed by variant id, and swaps the
 * visible cell values when the shopper selects a different variant.
 *
 * Legacy path: used only for products that have not been migrated to the
 * product-level `sizing.chart` metafield yet.
 *
 * Does not declare `requiredRefs` because a given product may only carry
 * "tops" measurements or only "bottoms" measurements — the set of ref
 * elements actually present in the DOM varies per product.
 *
 * @extends {Component}
 */
class SizeFitTableComponent extends Component {
  /** @type {Record<string, Record<string, string>>} */
  #dataMap = {};

  connectedCallback() {
    super.connectedCallback();
    this.#dataMap = this.#parseData();

    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    if (!target) return;
    target.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    if (!target) return;
    target.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /**
   * @returns {Record<string, Record<string, string>>}
   */
  #parseData() {
    const script = this.querySelector('script[data-size-fit-data]');
    if (!script?.textContent) return {};

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.warn('[size-fit-table] Failed to parse measurement data:', error);
      return {};
    }
  }

  /**
   * @param {import('@shopify/events').ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    event.promise
      .then(({ detail }) => {
        if (!detail) return;

        const { newProduct, resource, productId } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        }

        // Ignore selects bubbling up from an unrelated product on the page
        // (e.g. a different quick-add modal or product card).
        if (productId && this.dataset.productId && String(productId) !== String(this.dataset.productId)) {
          return;
        }

        if (!resource) return;

        const measurements = this.#dataMap[String(resource.id)];
        if (!measurements) return;

        for (const [key, value] of Object.entries(measurements)) {
          const cell = this.querySelector(`[ref="${key}"]`);
          if (!cell) continue;
          cell.textContent = value === '—' || value == null || value === '' ? '—' : `${value}"`;
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[size-fit-table] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('size-fit-table-component')) {
  customElements.define('size-fit-table-component', SizeFitTableComponent);
}

/**
 * A custom element that switches the product size chart between centimetres
 * and inches. Both values are rendered server-side from the cm source data,
 * so switching units is a pure CSS state change — no maths, no refetch.
 *
 * Marks itself with `data-js` on connect: without JavaScript the toggle stays
 * hidden and every cell shows "104 cm / 40.9 in", which is still correct.
 *
 * @extends {Component}
 */
class SizeChartComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.dataset.js = 'true';
    this.addEventListener('click', this.#handleClick);
    this.#syncButtons();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#handleClick);
  }

  /**
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    const origin = event.target instanceof Element ? event.target : null;
    const button = origin?.closest('[data-unit-btn]');
    if (!button || !this.contains(button)) return;

    const unit = button.getAttribute('data-unit-btn');
    if (unit !== 'cm' && unit !== 'in') return;

    this.dataset.unit = unit;
    this.#syncButtons();
  };

  #syncButtons() {
    const unit = this.dataset.unit === 'in' ? 'in' : 'cm';
    this.dataset.unit = unit;

    for (const button of this.querySelectorAll('[data-unit-btn]')) {
      button.setAttribute('aria-pressed', String(button.getAttribute('data-unit-btn') === unit));
    }
  }
}

if (!customElements.get('size-chart-component')) {
  customElements.define('size-chart-component', SizeChartComponent);
}
